import { Button } from "@/components/ui/button";
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { getClientFileDownloadUrlForAdmin, approveClientUploadFile, rejectClientUploadFile, unapproveClientUploadFile } from "@/service/weexpertService";
import { getDecodedFileName } from '@/lib/utils';
import {
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    useReactTable
} from "@tanstack/react-table";
import { format } from "date-fns";
import { DownloadIcon, EyeIcon, CheckIcon, XIcon, RotateCcwIcon, ArrowRightIcon } from "lucide-react";
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import TransferClientUploadFilePopup from '../popups/transfer-client-upload-file.popup';

const getStatusBadgeClasses = (status) => {
  switch (status) {
    case 'APPROVED':
      return 'bg-green-500 text-white hover:bg-green-600';
    case 'REJECTED':
      return 'bg-red-500 text-white hover:bg-red-600';
    case 'PENDING':
      return 'bg-yellow-500 text-white hover:bg-yellow-600';
    default:
      return 'bg-neutral-500 text-white hover:bg-neutral-600';
  }
};

const getStatusDisplayText = (status) => {
  switch (status) {
    case 'APPROVED':
      return 'Zatwierdzony';
    case 'REJECTED':
      return 'Odrzucony';
    case 'PENDING':
      return 'Oczekuje';
    default:
      return status;
  }
};

const SelfUploadedDocumentsTable = (props) => {
    const { clientUploadFiles = [], isLoading = false, onStatusChange } = props;
    const { t } = useTranslation();
    const [updatingStatus, setUpdatingStatus] = React.useState({});
    const [transferPopupOpen, setTransferPopupOpen] = React.useState(false);
    const [selectedFileForTransfer, setSelectedFileForTransfer] = React.useState(null);

    // Convert clientUploadFiles to the format expected by the table
    const selfUploadedDocuments = React.useMemo(() => {
        if (!Array.isArray(clientUploadFiles)) return [];
        return clientUploadFiles.map(uploadFile => ({
            id: uploadFile.id,
            entityType: uploadFile.type,
            createdAt: uploadFile.createdAt,
            status: uploadFile.status || 'PENDING',
            fileId: uploadFile.fileId,
            file: uploadFile.file,
            approvedAt: uploadFile.approvedAt,
            approvedBy: uploadFile.approvedBy,
            isTransferred: uploadFile.isTransferred || false,
            transferredAt: uploadFile.transferredAt,
            transferredBy: uploadFile.transferredBy,
            // Additional fields for compatibility
            uniqueId: uploadFile.type, // Use type as unique identifier
            validFrom: null,
            validTo: null
        }));
    }, [clientUploadFiles]);

    // Handle file download
    const handleDownload = async (uploadFile) => {
        try {
            const response = await getClientFileDownloadUrlForAdmin(uploadFile.id);
            const downloadUrl = response.data.url;

            // Create a temporary link and click it to download
            const link = document.createElement('a');
          link.target = "blank";
            link.href = downloadUrl;
            link.download = getDecodedFileName(uploadFile.file?.path) || 'document';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Download error:', error);
            // Could add toast notification here
        }
    };

    // Handle file approval
    const handleApprove = async (uploadFile) => {
        setUpdatingStatus(prev => ({ ...prev, [uploadFile.id]: 'approving' }));
        try {
            await approveClientUploadFile(uploadFile.id);
            onStatusChange && onStatusChange();
        } catch (error) {
            console.error('Approval error:', error);
        } finally {
            setUpdatingStatus(prev => ({ ...prev, [uploadFile.id]: null }));
        }
    };

    // Handle file rejection
    const handleReject = async (uploadFile) => {
        setUpdatingStatus(prev => ({ ...prev, [uploadFile.id]: 'rejecting' }));
        try {
            await rejectClientUploadFile(uploadFile.id);
            onStatusChange && onStatusChange();
        } catch (error) {
            console.error('Rejection error:', error);
        } finally {
            setUpdatingStatus(prev => ({ ...prev, [uploadFile.id]: null }));
        }
    };

    // Handle file unapprove (reset to pending)
    const handleUnapprove = async (uploadFile) => {
        setUpdatingStatus(prev => ({ ...prev, [uploadFile.id]: 'unapproving' }));
        try {
            await unapproveClientUploadFile(uploadFile.id);
            onStatusChange && onStatusChange();
        } catch (error) {
            console.error('Unapprove error:', error);
        } finally {
            setUpdatingStatus(prev => ({ ...prev, [uploadFile.id]: null }));
        }
    };

    // Handle transfer to internal documents
    const handleTransfer = (uploadFile) => {
        setSelectedFileForTransfer(uploadFile.id);
        setTransferPopupOpen(true);
    };

    const handleTransferComplete = () => {
        setSelectedFileForTransfer(null);
        onStatusChange && onStatusChange();
    };

    const columns = [
        {
            accessorKey: "entityType",
            header: t("document_type"),
            cell: ({ row }) => {
                const type = row.original.entityType;
                const typeLabels = {
                    'Passport': 'Paszport',
                    'Karta Pobytu': 'Karta pobytu',
                    'Decyzja': 'Decyzja',
                    'Visa': 'Wiza',
                    'Certyfikat rezydencji podatkowej': 'Certyfikat rezydencji podatkowej'
                };
                return (
                    <div className="capitalize">
                        {typeLabels[type] || type}
                    </div>
                );
            },
            key: uuidv4()
        },
        {
            accessorKey: "status",
            header: t("status"),
            cell: ({ row }) => {
                const status = row.original.status;
                return (
                    <div className="flex flex-col gap-1">
                        <Badge className={getStatusBadgeClasses(status)}>
                            {getStatusDisplayText(status)}
                        </Badge>
                        {row.original.approvedAt && (
                            <div className="text-xs text-gray-500">
                                {format(new Date(row.original.approvedAt), 'dd/MM/yyyy HH:mm')}
                                {row.original.approvedBy && (
                                    <div>przez: {row.original.approvedBy.firstName} {row.original.approvedBy.lastName}</div>
                                )}
                            </div>
                        )}
                        {row.original.isTransferred && (
                            <div className="text-xs text-blue-600 font-medium mt-1">
                                ✓ Przeniesiony do dokumentów wewnętrznych
                                {row.original.transferredAt && (
                                    <div className="text-gray-500">
                                        {format(new Date(row.original.transferredAt), 'dd/MM/yyyy HH:mm')}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            },
            key: uuidv4()
        },
        {
            accessorKey: "createdAt",
            header: t("upload_date"),
            cell: ({ row }) => (
                <div>
                    {format(new Date(row.original.createdAt), 'dd/MM/yyyy HH:mm')}
                </div>
            ),
            key: uuidv4()
        },
        {
            accessorKey: "validFrom",
            header: t("valid_from"),
            cell: ({ row }) => (
                <div>
                    {row.original.validFrom
                        ? format(new Date(row.original.validFrom), 'dd/MM/yyyy')
                        : t("not_specified")}
                </div>
            ),
            key: uuidv4()
        },
        {
            accessorKey: "validTo",
            header: t("valid_to"),
            cell: ({ row }) => (
                <div>
                    {row.original.validTo
                        ? format(new Date(row.original.validTo), 'dd/MM/yyyy')
                        : t("not_specified")}
                </div>
            ),
            key: uuidv4()
        },
        {
            accessorKey: "actions",
            header: t("actions"),
            cell: ({ row }) => {
                const status = row.original.status;
                const fileId = row.original.id;
                const isUpdating = updatingStatus[fileId];

                return (
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            className="w-10 h-10 p-0"
                            onClick={() => handleDownload(row.original)}
                            title="Pobierz"
                        >
                            <DownloadIcon className="h-4 w-4" />
                        </Button>

                        {status === 'PENDING' && (
                            <>
                                <Button
                                    variant="outline"
                                    className="w-10 h-10 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                    onClick={() => handleApprove(row.original)}
                                    disabled={isUpdating}
                                    title="Zatwierdź"
                                >
                                    <CheckIcon className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="outline"
                                    className="w-10 h-10 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleReject(row.original)}
                                    disabled={isUpdating}
                                    title="Odrzuć"
                                >
                                    <XIcon className="h-4 w-4" />
                                </Button>
                            </>
                        )}

                        {(status === 'APPROVED' || status === 'REJECTED') && (
                            <>
                                <Button
                                    variant="outline"
                                    className="w-10 h-10 p-0 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                    onClick={() => handleUnapprove(row.original)}
                                    disabled={isUpdating}
                                    title="Cofnij zatwierdzenie"
                                >
                                    <RotateCcwIcon className="h-4 w-4" />
                                </Button>

                                {status === 'APPROVED' && !row.original.isTransferred && (
                                    <Button
                                        variant="outline"
                                        className="w-10 h-10 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                        onClick={() => handleTransfer(row.original)}
                                        disabled={isUpdating}
                                        title="Przenieś do dokumentów wewnętrznych"
                                    >
                                        <ArrowRightIcon className="h-4 w-4" />
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                );
            },
            key: uuidv4()
        },
    ];

    const table = useReactTable({
        data: isLoading ? Array(3).fill({}) : selfUploadedDocuments,
        columns: isLoading ? columns.map((column) => {
            return {
                ...column,
                cell: () => {
                    return <Skeleton className='h-4 w-[150px]' />
                }
            }
        }) : columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        manualPagination: false,
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    // Don't render the section if there are no self-uploaded documents
    if (!isLoading && selfUploadedDocuments.length === 0) {
        return null;
    }

    return (
        <>
            <TransferClientUploadFilePopup
                open={transferPopupOpen}
                setOpen={setTransferPopupOpen}
                uploadFileId={selectedFileForTransfer}
                onTransferComplete={handleTransferComplete}
            />

            <div className="w-full bg-grayLightMainBg">
                <div className="text-xl font-medium py-6">
                    {t('self_uploaded_documents')}
                </div>
                <div className="rounded-md border p-2">
                    <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map(headerGroup => (
                            <TableRow key={uuidv4()}>
                                {headerGroup.headers.map(header => {
                                    return (
                                        <TableHead className="pt-3 pb-3"
                                            key={uuidv4()}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                        </TableHead>
                                    )
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map(row => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && "selected"}
                                >
                                    {row.getVisibleCells().map(cell => (
                                        <TableCell className="relative text-sm pt-3 pb-3"
                                            key={uuidv4()}>
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    className="h-24 text-center"
                                >
                                    {t('no_self_uploaded_documents')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                </div>
            </div>
        </>
    );
};

export default SelfUploadedDocumentsTable;
